const DEFAULT_OPFS_DB_NAME = 'app.db';
const SQLITE_HEADER = 'SQLite format 3\u0000';
type StorageEstimateWithUsageDetails = StorageEstimate & { usageDetails?: unknown };

export interface OpfsDatabaseProbeResult {
  exists: boolean;
  size: number | null;
  headerValid: boolean | null;
  failureReason?: 'missing' | 'empty' | 'invalid-header' | 'unsupported';
}

export interface OpfsStorageEstimateResult {
  usage: number | null;
  quota: number | null;
  usageDetails: Record<string, number> | null;
  error?: string;
}

export interface OpfsPersistenceStatusResult {
  persisted: boolean | null;
  error?: string;
}

export interface OpfsDirectoryDiagnostics {
  entries: string[] | null;
  isEmpty: boolean | null;
  error?: string;
}

export interface OpfsDatabaseDiagnostics {
  origin: string | null;
  isStandalone: boolean | null;
  wasDiscarded: boolean | null;
  persistence: OpfsPersistenceStatusResult;
  estimate: OpfsStorageEstimateResult;
  directory: OpfsDirectoryDiagnostics;
  database: OpfsDatabaseProbeResult & { error?: string };
}

function toErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function getWindowProperty<T>(factory: () => T): T | null {
  try {
    return factory();
  } catch (error) {
    console.warn('Failed to get window property', error);
    return null;
  }
}

function normalizeUsageDetails(usageDetails: unknown): Record<string, number> | null {
  if (!usageDetails || typeof usageDetails !== 'object') {
    return null;
  }

  const normalized: Record<string, number> = {};
  for (const [key, value] of Object.entries(usageDetails)) {
    if (typeof value === 'number') {
      normalized[key] = value;
    }
  }

  return normalized;
}

async function getPersistenceStatus(): Promise<OpfsPersistenceStatusResult> {
  if (typeof navigator === 'undefined' || typeof navigator.storage?.persisted !== 'function') {
    return { persisted: null };
  }

  try {
    return { persisted: await navigator.storage.persisted() };
  } catch (error) {
    return { persisted: null, error: toErrorMessage(error) };
  }
}

async function getStorageEstimate(): Promise<OpfsStorageEstimateResult> {
  if (typeof navigator === 'undefined' || typeof navigator.storage?.estimate !== 'function') {
    return {
      usage: null,
      quota: null,
      usageDetails: null,
    };
  }

  try {
    const estimate = (await navigator.storage.estimate()) as StorageEstimateWithUsageDetails;
    return {
      usage: estimate.usage ?? null,
      quota: estimate.quota ?? null,
      usageDetails: normalizeUsageDetails(estimate.usageDetails),
    };
  } catch (error) {
    return {
      usage: null,
      quota: null,
      usageDetails: null,
      error: toErrorMessage(error),
    };
  }
}

async function getDirectoryDiagnostics(): Promise<OpfsDirectoryDiagnostics> {
  if (typeof navigator === 'undefined' || typeof navigator.storage?.getDirectory !== 'function') {
    return {
      entries: null,
      isEmpty: null,
      error: 'OPFS API is not available in this environment.',
    };
  }

  try {
    const opfsRoot = await navigator.storage.getDirectory();
    const entries: string[] = [];

    if (typeof (opfsRoot as FileSystemDirectoryHandle & { entries?: unknown }).entries === 'function') {
      // TypeScript DOM libs still do not model OPFS entries() consistently.
      for await (const [name] of (
        opfsRoot as FileSystemDirectoryHandle & {
          entries: () => AsyncIterable<[string, FileSystemHandle]>;
        }
      ).entries()) {
        entries.push(name);
      }
    }

    entries.sort();

    return {
      entries,
      isEmpty: entries.length === 0,
    };
  } catch (error) {
    return {
      entries: null,
      isEmpty: null,
      error: toErrorMessage(error),
    };
  }
}

function isMissingFileError(error: unknown): boolean {
  return (
    (error instanceof DOMException && error.name === 'NotFoundError') ||
    (error instanceof Error && /not found/i.test(error.message))
  );
}

async function hasSqliteHeader(file: File): Promise<boolean> {
  if (file.size < SQLITE_HEADER.length) {
    return false;
  }

  const headerBuffer = await file.slice(0, SQLITE_HEADER.length).arrayBuffer();
  const header = new TextDecoder().decode(headerBuffer);
  return header === SQLITE_HEADER;
}

export async function probeOpfsDatabaseFile(dbName = DEFAULT_OPFS_DB_NAME): Promise<OpfsDatabaseProbeResult> {
  if (typeof navigator === 'undefined' || typeof navigator.storage?.getDirectory !== 'function') {
    return {
      exists: false,
      size: null,
      headerValid: null,
      failureReason: 'unsupported',
    };
  }

  try {
    const opfsRoot = await navigator.storage.getDirectory();
    const dbHandle = await opfsRoot.getFileHandle(dbName, { create: false });
    const file = await dbHandle.getFile();

    if (file.size === 0) {
      return {
        exists: true,
        size: 0,
        headerValid: false,
        failureReason: 'empty',
      };
    }

    const headerValid = await hasSqliteHeader(file);
    const probe: OpfsDatabaseProbeResult = {
      exists: true,
      size: file.size,
      headerValid,
    };

    if (!headerValid) {
      probe.failureReason = 'invalid-header';
    }

    return probe;
  } catch (error) {
    if (isMissingFileError(error)) {
      return {
        exists: false,
        size: null,
        headerValid: null,
        failureReason: 'missing',
      };
    }

    throw error;
  }
}

export async function collectOpfsDatabaseDiagnostics(dbName = DEFAULT_OPFS_DB_NAME): Promise<OpfsDatabaseDiagnostics> {
  const [persistence, estimate, directory] = await Promise.all([
    getPersistenceStatus(),
    getStorageEstimate(),
    getDirectoryDiagnostics(),
  ]);

  let database: OpfsDatabaseDiagnostics['database'];
  try {
    database = await probeOpfsDatabaseFile(dbName);
  } catch (error) {
    database = {
      exists: false,
      size: null,
      headerValid: null,
      error: toErrorMessage(error),
    };
  }

  return {
    origin: getWindowProperty(() => location.origin),
    isStandalone: getWindowProperty(() => window.matchMedia('(display-mode: standalone)').matches),
    wasDiscarded: getWindowProperty(() => Boolean((document as Document & { wasDiscarded?: boolean }).wasDiscarded)),
    persistence,
    estimate,
    directory,
    database,
  };
}

export function logOpfsDatabaseDiagnostics(
  context: string,
  diagnostics: OpfsDatabaseDiagnostics,
  level: 'info' | 'warn' = 'info',
): void {
  console[level](`[OPFSDiagnostics] ${context}`, diagnostics);
}

export async function assertExistingOpfsDatabaseFile(dbName = DEFAULT_OPFS_DB_NAME): Promise<OpfsDatabaseProbeResult> {
  const probe = await probeOpfsDatabaseFile(dbName);

  switch (probe.failureReason) {
    case undefined:
      return probe;
    case 'missing':
      throw new Error(`Expected existing OPFS database "${dbName}", but the file was not found.`);
    case 'empty':
      throw new Error(`Expected existing OPFS database "${dbName}", but the file is empty.`);
    case 'invalid-header':
      throw new Error(`Expected existing OPFS database "${dbName}", but the file does not look like SQLite.`);
    case 'unsupported':
      throw new Error('OPFS API is not available in this environment.');
  }
}
